import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileText, Plus, Search, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, EmptyState, PageHeader, TableSkeleton } from "../components/ui";
import { formatDate, formatMoney } from "../lib/format";
import { isManagerOrAbove } from "../lib/people";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";

const FILTERS = ["", "issued", "refunded", "void"] as const;

const STATUS_TONE: Record<string, "emerald" | "amber" | "slate" | "rose"> = {
  issued: "amber",
  refunded: "emerald",
  void: "slate",
};

/**
 * The credit-note register.
 *
 * A credit note is a correction to an issued invoice, never an edit of it, so
 * this is a list of documents with their own numbers and their own archive.
 */
export function CreditNotesPage() {
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(params.get("search") ?? "");

  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  const cursor = params.get("cursor") ?? "";

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  const query = useQuery({
    queryKey: ["credit-notes", search, status, cursor],
    queryFn: ({ signal }) =>
      api.creditNotes(
        {
          search: search || undefined,
          status: status || undefined,
          cursor: cursor || undefined,
          page_size: 25,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const notes = query.data?.results ?? [];
  const nextCursor = cursorFrom(query.data?.next);
  const filtering = Boolean(search || status);

  return (
    <>
      <PageHeader
        title="Credit notes"
        subtitle="Corrections to issued invoices, with their own numbers and history."
        actions={
          isManagerOrAbove(user?.role) ? (
            <Link
              to="/billing/credit-notes/new"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus size={14} /> New credit note
            </Link>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              update({ search: event.target.value, cursor: "" });
            }}
            placeholder="Search by credit note number"
            aria-label="Search credit notes"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((value) => (
            <button
              key={value || "all"}
              onClick={() => update({ status: value, cursor: "" })}
              className={
                status === value
                  ? "rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium capitalize text-white"
                  : "rounded-full bg-white px-3 py-1.5 text-xs font-medium capitalize text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              }
            >
              {value || "All"}
            </button>
          ))}
        </div>
        {filtering ? (
          <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <X size={14} /> Clear
          </Button>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <TableSkeleton rows={8} />
        ) : query.isError ? (
          <EmptyState
            title="Could not load credit notes"
            body={query.error instanceof ApiError ? query.error.message : "Please try again."}
          />
        ) : notes.length === 0 ? (
          <EmptyState
            icon={<FileText size={28} />}
            title={filtering ? "Nothing matches that" : "No credit notes yet"}
            body={
              filtering
                ? "Try a different number, or clear the filters."
                : "Credit notes you issue will appear here."
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Number</th>
                <th className="px-4 py-2.5">Invoice</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Reason</th>
                <th className="px-4 py-2.5 text-right">Total</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {notes.map((note) => (
                <tr
                  key={note.id}
                  onClick={() => navigate(`/billing/credit-notes/${note.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-3 text-sm font-medium tabular-nums text-slate-900">
                    <Link
                      to={`/billing/credit-notes/${note.id}`}
                      className="hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {note.number || "Credit note"}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    {note.invoice_number || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{note.contact_name || "—"}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    {formatDate(note.issue_date, timeZone)}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize text-slate-600">
                    {note.reason?.replace(/_/g, " ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-900">
                    {formatMoney(note.total_amount)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[note.status] ?? "slate"}>
                      <span className="capitalize">{note.status}</span>
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <nav className="mt-4 flex items-center justify-between">
        <Button onClick={() => update({ cursor: "" })} disabled={!cursor || query.isFetching}>
          <ChevronLeft size={16} /> First
        </Button>
        <span className="text-xs text-slate-500">
          {query.isFetching ? "Loading." : cursor ? "Next page" : "First page"}
        </span>
        <Button
          onClick={() => nextCursor && update({ cursor: nextCursor })}
          disabled={!nextCursor || query.isFetching}
        >
          Next <ChevronRight size={16} />
        </Button>
      </nav>
    </>
  );
}

/** `next` is an absolute URL; the cursor is its trailing query parameter. */
function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get("cursor") ?? undefined;
}
